import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandLockup, LitraMark } from "@/components/Brand";
import DecodeMiniCard from "@/components/DecodeMiniCard";
import { ScanText, BookOpen, Gamepad2, Orbit, Wand2, ArrowRight, Loader2 } from "lucide-react";

const MODES = [
  { icon: ScanText, name: "Decode", blurb: "Read the architecture of any name, brand, or word." },
  { icon: BookOpen, name: "Doctrine", blurb: "Year 1–10 lessons. Elite education for anybody." },
  { icon: Gamepad2, name: "Path", blurb: "A symbolic development journey. Every choice teaches the doctrine." },
  { icon: Orbit, name: "Universe", blurb: "Explore realms and unlock hidden rooms of the doctrine." },
  { icon: Wand2, name: "Studio", blurb: "Brand names, taglines, captions — engineered." },
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [word, setWord] = useState("Power");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const runDemo = async () => {
    if (!word.trim()) return;
    setLoading(true);
    try {
      const r = await api.post("/decode/mini", { text: word, decode_type: "Personal Identity" });
      setResult(r.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  const s = settings || {};
  const start = () => navigate(user ? "/today" : "/login");

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-[var(--bone)]">
      {/* Nav */}
      <header className="sticky top-0 z-30 glass border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <BrandLockup />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="home-signin-button" className="text-[var(--bone-muted)]">Sign in</Button>
            <Button onClick={start} className="bg-[var(--primary)] hover:opacity-90" data-testid="home-get-started-button">Get started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden hero-sheen">
        <div className="grain" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="fade-in">
            <div className="mono-chip text-[var(--bone-faint)] mb-4">{s.definition || "Litrametry is the study of how letters behave inside meaning."}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold leading-[1.05]">
              {s.hero_headline || "Every word is a room. Every letter is a key."}
            </h1>
            <p className="mt-5 text-lg text-[var(--bone-muted)] leading-relaxed" style={{ maxWidth: "56ch" }}>
              {s.hero_subheadline || "Litrametry is an interactive doctrine for decoding names, brands, culture, identity, and meaning through the architecture of letters."}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" onClick={start} className="bg-[var(--primary)] hover:opacity-90" data-testid="home-start-litrametry-button">
                {s.cta_primary || "Start Litrametry"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
                className="glass border-[rgba(185,192,204,0.25)]" data-testid="home-decode-a-word-button">
                {s.cta_secondary || "Decode a Word"}
              </Button>
            </div>
            <p className="mt-6 text-sm text-[var(--bone-faint)]">{s.hero_secondary || "Play through life. Study the doctrine. Decode the wordbody. Build your language intelligence."}</p>
          </div>

          {/* Inline demo */}
          <div id="demo" className="fade-in">
            <div className="litra-card p-5">
              <div className="text-sm text-[var(--bone-muted)] mb-2">Try the Decode Engine</div>
              <div className="flex gap-2">
                <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="Type a name or word…"
                  className="bg-white/[0.04] border-white/[0.1]" data-testid="home-demo-input"
                  onKeyDown={(e) => e.key === "Enter" && runDemo()} />
                <Button onClick={runDemo} className="bg-[var(--primary)] hover:opacity-90" data-testid="home-demo-decode-button" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decode"}
                </Button>
              </div>
              <div className="mt-4">
                {result ? <DecodeMiniCard data={result} decodeType="Personal Identity" /> : (
                  <div className="text-sm text-[var(--bone-faint)] p-4 text-center">Decode any word to see its Litraroot, Litraseal, and signals.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Doctrine lines */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid sm:grid-cols-5 gap-3">
          {(s.doctrine_lines || ["A letter is a function.", "A word is a body.", "A sound is a force.", "A sequence is a behavior.", "Meaning is the outcome."]).map((line, i) => (
            <div key={i} className="litra-card p-4 text-center">
              <div className="mono-chip green-text mb-1">0{i + 1}</div>
              <div className="text-sm">{line}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Modes */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-2xl font-display font-semibold mb-6">Five ways to learn the doctrine</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {MODES.map((m) => (
            <div key={m.name} className="litra-card litra-card-hover p-5">
              <m.icon className="w-6 h-6 green-text" />
              <div className="mt-3 font-display font-semibold">{m.name}</div>
              <p className="mt-1 text-sm text-[var(--bone-muted)]">{m.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { name: "Free", price: "$0", note: "Start decoding today" },
            { name: "Student", price: "$4.99", note: "Unlimited learning", featured: true },
            { name: "Creator", price: "$9.99", note: "Pro creator tools" },
          ].map((p) => (
            <div key={p.name} className={`litra-card p-6 ${p.featured ? "border-[rgba(20,90,67,0.6)]" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-semibold">{p.name}</div>
                {p.featured && <span className="mono-chip green-text">POPULAR</span>}
              </div>
              <div className="text-3xl font-display font-semibold mt-2">{p.price}<span className="text-sm text-[var(--bone-faint)]">/mo</span></div>
              <p className="mt-1 text-sm text-[var(--bone-muted)]">{p.note}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 text-center">
          <Button variant="outline" onClick={start} className="glass border-[rgba(185,192,204,0.25)]" data-testid="home-view-pricing-button">See all plans</Button>
        </div>
      </section>

      {/* Footer + disclaimer */}
      <footer className="border-t border-white/[0.06] mt-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-2 mb-3"><LitraMark size={22} /><span className="font-display font-semibold">Litrametry</span></div>
          <p className="text-xs text-[var(--bone-faint)] italic" style={{ maxWidth: "80ch" }} data-testid="home-disclaimer">
            {s.disclaimer || "Litrametry is a symbolic and interpretive framework for language, identity, branding, and cultural analysis. It is not medical, legal, financial, psychological, or investment advice."}
          </p>
        </div>
      </footer>
    </div>
  );
}
